import { Badge } from "@package/subframe/components/Badge";
import { FeatherAlertCircle, FeatherCheck, FeatherX } from "@subframe/core";
import { formatDistanceToNow } from "date-fns";

import type { FollowUpActionResponseDto } from "@/generated/model";
import { FollowUpActionResponseDtoStatus } from "@/generated/model";
import { getUkTimeNow, getUkTodayStr, toUkDateStr } from "@/lib/date-utils";

/** Convert an API dueDate (ISO string) to a "YYYY-MM-DD" UK date string */
export function dueDateToUkStr(dueDate: string): string {
  return toUkDateStr(new Date(dueDate));
}

/** Check if a follow-up action is overdue (by status or past due deadline) */
export function isActionOverdue(action: FollowUpActionResponseDto): boolean {
  if (action.status === FollowUpActionResponseDtoStatus.OVERDUE) return true;
  if (!action.dueDate) return false;

  const dueDateStr = dueDateToUkStr(action.dueDate);
  const ukToday = getUkTodayStr();

  // Due date is in the past → overdue
  if (dueDateStr < ukToday) return true;

  // Due date is today → check time
  if (dueDateStr === ukToday && action.dueTime) {
    return action.dueTime < getUkTimeNow();
  }

  return false;
}

/** Get the status badge for a follow-up action */
export function getActionStatusBadge(
  status: FollowUpActionResponseDtoStatus,
): React.ReactNode {
  switch (status) {
    case FollowUpActionResponseDtoStatus.OVERDUE:
      return (
        <Badge variant="error" icon={<FeatherAlertCircle />}>
          Overdue
        </Badge>
      );
    case FollowUpActionResponseDtoStatus.COMPLETED:
      return (
        <Badge variant="success" icon={<FeatherCheck />}>
          Completed
        </Badge>
      );
    case FollowUpActionResponseDtoStatus.CANCELLED:
      return (
        <Badge variant="error" icon={<FeatherX />}>
          Cancelled
        </Badge>
      );
    case FollowUpActionResponseDtoStatus.WAITING_APPROVAL:
      return <Badge>Waiting approval</Badge>;
    default:
      return <Badge variant="neutral">Open</Badge>;
  }
}

/** Format the due date display text for a follow-up action */
export function formatActionDueDate(
  action: FollowUpActionResponseDto,
): string | null {
  if (!action.dueDate) return null;

  const ukDateStr = dueDateToUkStr(action.dueDate);
  const parts = ukDateStr.split("-").map(Number);
  const dueDate = new Date(parts[0]!, parts[1]! - 1, parts[2]!);

  // If there's a due time, build a precise deadline datetime
  if (action.dueTime) {
    const [hours, minutes] = action.dueTime.split(":").map(Number);
    dueDate.setHours(hours!, minutes!);
  }

  if (isActionOverdue(action)) {
    return `${formatDistanceToNow(dueDate)} ago`;
  }

  return `Due in ${formatDistanceToNow(dueDate)}`;
}

/** Filter to only active (non-completed, non-cancelled) actions */
export function filterActiveActions(
  actions: FollowUpActionResponseDto[],
): FollowUpActionResponseDto[] {
  return actions.filter(
    (a) =>
      a.status !== FollowUpActionResponseDtoStatus.COMPLETED &&
      a.status !== FollowUpActionResponseDtoStatus.CANCELLED,
  );
}

/** Sort actions by urgency: OVERDUE → WAITING_APPROVAL → OPEN, then by dueDate */
export function sortActionsByUrgency(
  actions: FollowUpActionResponseDto[],
): FollowUpActionResponseDto[] {
  return [...actions].sort((a, b) => {
    const statusPriority = (s: FollowUpActionResponseDtoStatus) =>
      s === FollowUpActionResponseDtoStatus.OVERDUE
        ? 0
        : s === FollowUpActionResponseDtoStatus.WAITING_APPROVAL
          ? 1
          : 2;
    const diff = statusPriority(a.status) - statusPriority(b.status);
    if (diff !== 0) return diff;
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });
}

export type DashboardActionGroup = "late" | "open" | "pending" | "complete";

export interface GroupedDashboardActions {
  late: FollowUpActionResponseDto[];
  open: FollowUpActionResponseDto[];
  pending: FollowUpActionResponseDto[];
  complete: FollowUpActionResponseDto[];
}

/** Group actions by dashboard status sections and sort within each group */
export function groupActionsForDashboard(
  actions: FollowUpActionResponseDto[],
): GroupedDashboardActions {
  const late: FollowUpActionResponseDto[] = [];
  const open: FollowUpActionResponseDto[] = [];
  const pending: FollowUpActionResponseDto[] = [];
  const complete: FollowUpActionResponseDto[] = [];

  for (const action of actions) {
    if (action.status === FollowUpActionResponseDtoStatus.CANCELLED) continue;

    if (
      action.status === FollowUpActionResponseDtoStatus.OVERDUE ||
      (action.status === FollowUpActionResponseDtoStatus.OPEN && isActionOverdue(action))
    ) {
      late.push(action);
    } else if (action.status === FollowUpActionResponseDtoStatus.OPEN) {
      open.push(action);
    } else if (action.status === FollowUpActionResponseDtoStatus.WAITING_APPROVAL) {
      pending.push(action);
    } else if (action.status === FollowUpActionResponseDtoStatus.COMPLETED) {
      complete.push(action);
    }
  }

  // Late: overdue the longest first (earliest dueDate first)
  late.sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });

  // Open: nearest due date first
  open.sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });

  // Pending: nearest due date first
  pending.sort((a, b) => {
    if (a.dueDate && b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    if (a.dueDate) return -1;
    if (b.dueDate) return 1;
    return 0;
  });

  // Complete: most recently completed first
  complete.sort((a, b) => {
    if (a.completedAt && b.completedAt) return b.completedAt.localeCompare(a.completedAt);
    if (a.completedAt) return -1;
    if (b.completedAt) return 1;
    return 0;
  });

  return { late, open, pending, complete };
}

/** Format a due date/time as a human-readable string like "Mon 10 Mar, 2:30 PM" */
export function formatActionDueDateTime(
  action: FollowUpActionResponseDto,
): string | null {
  if (!action.dueDate) return null;

  const ukDateStr = dueDateToUkStr(action.dueDate);
  const parts = ukDateStr.split("-").map(Number);
  const dueDate = new Date(parts[0]!, parts[1]! - 1, parts[2]!);

  const dayName = dueDate.toLocaleDateString("en-GB", { weekday: "short" });
  const day = dueDate.getDate();
  const month = dueDate.toLocaleDateString("en-GB", { month: "short" });

  if (action.dueTime) {
    const [hours, minutes] = action.dueTime.split(":").map(Number);
    const period = hours! >= 12 ? "PM" : "AM";
    const displayHour = hours! % 12 || 12;
    const timeStr = minutes ? `${displayHour}:${String(minutes).padStart(2, "0")} ${period}` : `${displayHour} ${period}`;
    return `${dayName} ${day} ${month}, ${timeStr}`;
  }

  return `${dayName} ${day} ${month}`;
}
